import { ReactElement, ReactNode, useMemo, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ColumnOrderState, VisibilityState } from '@tanstack/react-table';
import styled from 'styled-components';

import { Button } from '@components/button';
import { usePreviousValue } from '@components/component-helpers';
import { EmptySearchState } from '@components/empty-search-state';
import { FocusBox } from '@components/focus-box';
import { SmallDragHandle } from '@components/form-builder/drag-handle';
import { IconProps } from '@components/icons';
import { IconColumns } from '@components/icons/columns';
import { IconDrag } from '@components/icons/drag';
import { InfoTooltip } from '@components/info-tooltip';
import { SearchBox } from '@components/search-box';
import { Sidebar, showSidebar } from '@components/sidebar';
import { Horizontal, Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { SmallColumnIcon } from './column-icon';
import { getColumnOrderIndex } from './column-order';
import { ensureSmallIcon } from './table-utils';
import { AvailableColumn, DataItemType } from './types';

export const showFieldsSidebar = <T extends DataItemType>(props: FieldsSidebarProps<T>) => {
  showSidebar({
    component: FieldsSidebar<T>,
    options: { hasBackdrop: true, variant: 'REGULAR' },
    props,
  });
};

type FieldsSidebarProps<T extends DataItemType> = {
  availableColumns: Array<AvailableColumn<T> & { extraContent?: ReactNode }>;
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
  onVisibilityUpdate(newState: VisibilityState): void;
  onOrderUpdate(newOrder: ColumnOrderState): void;
};

export const FieldsSidebar = <T extends DataItemType>({
  availableColumns,
  columnVisibility: currentColumnVisibility,
  columnOrder: currentColumnOrder,
  onOrderUpdate,
  onVisibilityUpdate,
}: FieldsSidebarProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const [columnVisibility, setColumnVisibility] = useState(currentColumnVisibility);
  const [columnOrder, setColumnOrder] = useState(currentColumnOrder);

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('title')} icon={IconColumns} iconColor="elementBlue100" />
      <Sidebar.Content>
        <ColumnManager
          availableColumns={availableColumns}
          columnVisibility={columnVisibility}
          columnOrder={columnOrder}
          onOrderUpdate={(order) => {
            setColumnOrder(order);
            onOrderUpdate(order);
          }}
          onVisibilityUpdate={(visibility) => {
            setColumnVisibility(visibility);
            onVisibilityUpdate(visibility);
          }}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

export type ColumnManagerProps<T extends DataItemType> = FieldsSidebarProps<T>;

export const ColumnManager = <T extends DataItemType>({
  availableColumns,
  columnVisibility,
  columnOrder,
  onOrderUpdate,
  onVisibilityUpdate,
}: ColumnManagerProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState<UniqueIdentifier>();
  const sensors = useSensors(useSensor(PointerSensor));

  const columns = useMemo(() => {
    const newColumns = availableColumns.slice().sort((a, b) => {
      return getColumnOrderIndex(columnOrder, a.id) - getColumnOrderIndex(columnOrder, b.id);
    });
    return newColumns;
  }, [availableColumns, columnOrder]);

  const oldColumns = usePreviousValue(columns.map((column) => column.id));

  const newIds = useMemo(() => {
    if (!oldColumns) {
      // First render, previous value hasnt been set
      return [];
    }
    return columns
      .filter((column) => {
        return !oldColumns.includes(column.id);
      })
      .map((column) => column.id);
  }, [columns, oldColumns]);

  const onDragEnd = (event: DragEndEvent) => {
    setActiveId(undefined);
    const { active, over } = event;
    if (!over) {
      return;
    }

    const activeIndex = columnOrder.indexOf(String(active.id));
    const overIndex = columnOrder.indexOf(String(over.id));
    if (activeIndex === -1 || overIndex === -1) {
      return;
    }

    const newOrder = arrayMove(columnOrder, activeIndex, overIndex);
    onOrderUpdate(newOrder);
  };

  const onToggleAll = (isVisible: boolean = true) => {
    const newVisibility = availableColumns
      .map((column) => column.id)
      .reduce((visibility, columnId) => {
        visibility[columnId] = isVisible;
        return visibility;
      }, {});
    if (!isVisible && columnOrder.length > 0) {
      newVisibility[columnOrder[0]] = true;
    }
    onVisibilityUpdate(newVisibility);
  };

  const toggleVisibility = (columnId: string) => {
    const prev = columnVisibility[columnId];
    const hiddenCount = Object.keys(columnVisibility).filter(
      (key) => columnVisibility[key] === false
    ).length;

    if (hiddenCount >= columns.length - 1 && prev !== false) {
      return;
    }
    const newVisibility = {
      ...columnVisibility,
      [columnId]: prev === undefined ? false : !prev,
    };
    onVisibilityUpdate(newVisibility);
  };

  const searchStr = search.toLowerCase();
  const filteredColumns = columns.filter((column) => {
    return column.title.toLowerCase().includes(searchStr);
  });

  const activeColumn = activeId ? filteredColumns.find((column) => column.id === activeId) : null;

  return (
    <Stack space={4}>
      <Stack orientation="horizontal" space={2}>
        <SearchBox placeholder={t('searchPlaceholder')} onChange={setSearch} />
        <Button text={t('showAll')} variant="secondary" onClick={() => onToggleAll(true)} />
        <Button text={t('hideAll')} variant="secondary" onClick={() => onToggleAll(false)} />
      </Stack>

      {searchStr && filteredColumns.length === 0 && (
        <EmptySearchState variant="small" orientation="horizontal" skipMargin />
      )}

      <DndContext
        sensors={sensors}
        onDragEnd={onDragEnd}
        onDragStart={(event) => setActiveId(event.active.id)}
        onDragCancel={() => setActiveId(undefined)}
      >
        <DragOverlay>
          {activeColumn && (
            <OverlayContainer>
              <OverlayRow>
                <OverlaySwitchWrapper>
                  <Switch
                    size="sm"
                    label={activeColumn.title}
                    labelVisible={false}
                    key={activeColumn.id}
                    value={columnVisibility[activeColumn.id] !== false}
                  />
                </OverlaySwitchWrapper>
                <OverlayContentRow>
                  {activeColumn.renderHeaderIcon ? (
                    <IconWrapper>{activeColumn.renderHeaderIcon()}</IconWrapper>
                  ) : activeColumn.type ? (
                    <IconWrapper>
                      <SmallColumnIcon type={activeColumn.type} />
                    </IconWrapper>
                  ) : null}
                  <ColumnTitleText font="regular12" text={activeColumn.title} />
                </OverlayContentRow>
              </OverlayRow>
              <SmallDragHandle icon={<IconDrag size={16} />} />
            </OverlayContainer>
          )}
        </DragOverlay>
        <SortableContext items={columns.map((column) => column.id)}>
          <div>
            {filteredColumns.map((column, index) => {
              const icon = column.renderHeaderIcon ? (
                ensureSmallIcon(column.renderHeaderIcon())
              ) : column.type ? (
                <SmallColumnIcon type={column.type} />
              ) : undefined;
              return (
                <FocusBox isFocused={newIds.includes(column.id)} key={column.id}>
                  <ColumnItem
                    icon={icon}
                    index={index}
                    column={column}
                    onToggle={toggleVisibility}
                    isActive={columnVisibility[column.id] !== false}
                    isFiltered={search.length > 0}
                  />
                </FocusBox>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </Stack>
  );
};

type ColumnItemProps<T extends DataItemType> = {
  onToggle(columnId: string): void;
  column: AvailableColumn<T> & { extraContent?: ReactNode };
  index: number;
  isActive: boolean;
  isFiltered: boolean;
  icon?: ReactElement<IconProps>;
};

const ColumnItem = <T extends DataItemType>({
  column,
  onToggle,
  index,
  isActive,
  isFiltered,
  icon,
}: ColumnItemProps<T>) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
    data: {
      order: index,
    },
  });

  return (
    <ItemContainer
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
      }}
    >
      <ItemRow>
        <ItemSwitchWrapper>
          <Switch
            size="sm"
            label={column.title}
            labelVisible={false}
            key={column.id}
            value={isActive}
            onChange={() => onToggle(column.id)}
          />
        </ItemSwitchWrapper>
        <ItemContentRow>
          {icon && <IconWrapper>{icon}</IconWrapper>}
          <ColumnTitleText font="regular12" text={column.title} />
        </ItemContentRow>
      </ItemRow>
      <Stack space={1} orientation="horizontal" align="flex-start">
        {column.tooltip && (
          <TooltipAlignWrapper>
            <InfoTooltip tooltip={column.tooltip()} placement="left" />
          </TooltipAlignWrapper>
        )}
        {column.extraContent}
        {!isFiltered && (
          <SmallDragHandle {...attributes} {...listeners} icon={<IconDrag size={16} />} />
        )}
      </Stack>
    </ItemContainer>
  );
};

const locale = {
  en: {
    title: 'Show / Hide fields',
    showAll: 'Show all',
    hideAll: 'Hide all',
    searchPlaceholder: 'Search fields...',
  },
  is: {
    title: 'Sýna/Fela Dálka',
    showAll: 'Sýna alla',
    hideAll: 'Fela alla',
    searchPlaceholder: 'Leita að dálk...',
  },
};

const ItemContainer = styled(Stack).attrs({
  justify: 'space-between',
  orientation: 'horizontal',
})({
  padding: `${pxToRem(4)} 0`,
});

const OverlayContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
  align: 'flex-start',
})({
  boxShadow: theme.shadow.medium,
  borderRadius: pxToRem(4),
  margin: `0 ${pxToRem(-4)}`,
  backgroundColor: theme.color.backgroundLightDark,
  padding: pxToRem(4),
});

const OverlayRow = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'flex-start',
  space: 2,
})({
  minWidth: 0,
  flexWrap: 'nowrap',
});

const OverlaySwitchWrapper = styled.div({
  alignSelf: 'flex-start',
});

const OverlayContentRow = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'flex-start',
  space: 1,
})({
  flex: 1,
  minWidth: 0,
  flexWrap: 'nowrap',
  paddingTop: pxToRem(4),
});

const IconWrapper = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  '--icon-color': theme.color.elementPurple50,
});

const ColumnTitleText = styled(Text)({
  whiteSpace: 'normal',
  alignSelf: 'flex-start',
});

const ItemRow = styled(Horizontal).attrs({
  align: 'flex-start',
  space: 2,
})({
  minWidth: 0,
  flexWrap: 'nowrap',
});

const ItemSwitchWrapper = styled.div({
  alignSelf: 'flex-start',
});

const TooltipAlignWrapper = styled.div({
  display: 'flex',
  height: pxToRem(24),
  alignItems: 'center',
});

const ItemContentRow = styled(Horizontal).attrs({
  space: 1,
  align: 'flex-start',
})({
  flex: 1,
  minWidth: 0,
  flexWrap: 'nowrap',
  paddingTop: pxToRem(4),
});
